import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import { getAuthToken, isTokenExpired, clearAuthToken } from '../api/config';

// Public & auth pages
import LoadingPage from '../pages/LoadingPage';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import VerifyEmailCallbackPage from '../pages/VerifyEmailCallbackPage';
import VerifyEmailPendingPage from '../pages/VerifyEmailPendingPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import PrivacyPage from '../pages/PrivacyPage';
import TermsPage from '../pages/TermsPage';
import RefundPage from '../pages/RefundPage';
import AIDisclaimerPage from '../pages/AIDisclaimerPage';
import PhotoConsentPage from '../pages/PhotoConsentPage';
import AffiliateDisclosurePage from '../pages/AffiliateDisclosurePage';
import CookiePolicyPage from '../pages/CookiePolicyPage';
import DataRetentionPage from '../pages/DataRetentionPage';
import NotFoundPage from '../pages/NotFoundPage';

// NEW: your public landing page
import LandingPageNew from '../pages/LandingPageNew';

// App (protected) pages
import ProfileCompletionPage from '../pages/ProfileCompletionPage';
import HomePage from '../pages/HomePage';
import NewAnalysisPage from '../pages/NewAnalysisPage';
import PendingPage from '../pages/PendingPage';
import AnalysisResultPage from '../pages/AnalysisResultPage';
import HistoryPage from '../pages/HistoryPage';
import PlansPage from '../pages/PlansPage';
import ProfilePage from '../pages/ProfilePage';
import DeleteAccountPage from '../pages/DeleteAccountPage';
import DeleteAccountConfirmPage from '../pages/DeleteAccountConfirmPage';
import DeleteAccountCancelPage from '../pages/DeleteAccountCancelPage';
import ProfileEditPage from '../pages/ProfileEditPage';
import ProfilePlanPage from '../pages/ProfilePlanPage';
import WalletPage from '../pages/WalletPage';
import SupportPage from '../pages/SupportPage';
import PublicSupportPage from '../pages/PublicSupportPage';
import AboutPage from '../pages/AboutPage';
import CaptureGuidePage from '../pages/CaptureGuidePage';
import ProductScanPage from '../pages/ProductScanPage';
import ProductResultPage from '../pages/ProductResultPage';
import NewHubPage from '../pages/NewHubPage';
import FavoritesPage from '../pages/FavoritesPage';
import FavoriteProductDetailPage from '../pages/FavoriteProductDetailPage';
import ProductCompatibilityScanPage from '../pages/ProductCompatibilityScanPage';
import ProductCompatibilityResultPage from '../pages/ProductCompatibilityResultPage';
import ProductComparisonScanPage from '../pages/ProductComparisonScanPage';
import ProductComparisonResultPage from '../pages/ProductComparisonResultPage';
import ProductScannerInfoPage from '../pages/ProductScannerInfoPage';
import ProductsPage from '../pages/ProductsPage';
import IngredientsPage from '../pages/IngredientsPage';
import IngredientDetailsPage from '../pages/IngredientDetailsPage';
import DataExportDownloadPage from '../pages/DataExportDownloadPage';

import WeatherScanPage from '../pages/WeatherScanPage';
import WeatherResultPage from '../pages/WeatherResultPage';
import PremiumAnalysisPage from '../pages/PremiumAnalysisPage';
import PremiumAnalysisResultPage from '../pages/PremiumAnalysisResultPage';
import PremiumAnalysisPDFPage from '../pages/PremiumAnalysisPDFPage';
import AnalysisPDFPage from '../pages/AnalysisPDFPage';
import HairAnalysisPDFPage from '../pages/HairAnalysisPDFPage';
import MakeupAnalysisPDFPage from '../pages/MakeupAnalysisPDFPage';
import AIChatPage from '../pages/AIChatPage';
import AssistantExperimentalGatePage, { AI_CHAT_EXPERIMENT_ACK_KEY } from '../pages/AssistantExperimentalGatePage';
import KivoBallDemoPage from '../pages/KivoBallDemoPage';
import HairAnalysisPage from '../pages/HairAnalysisPage';
import HairAnalysisResultPage from '../pages/HairAnalysisResultPage';
import MakeupAnalysisPage from '../pages/MakeupAnalysisPage';
import MakeupAnalysisResultPage from '../pages/MakeupAnalysisResultPage';

function ProtectedRoute({ children }: { children: React.JSX.Element }) {
  const location = useLocation();
  const token = getAuthToken();

  if (!token || isTokenExpired(token)) {
    clearAuthToken();
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

/**
 * Root shell:
 * - If path is exactly "/" and user is NOT authed → show LandingPage
 * - Otherwise → render the protected app (MainLayout + Outlet)
 *
 * This keeps ALL your existing routes unchanged, and "/" still becomes HomePage
 * for logged-in users. After login, `navigateTo('/')` keeps working.
 */
function RootShell(): React.JSX.Element {
  const token = getAuthToken();
  const authed = token && !isTokenExpired(token);
  const { pathname } = useLocation();

  // Public landing only on the exact root when logged-out
  if (pathname === '/' && !authed) {
    return <LandingPageNew />;
  }

  // Everything else (including "/" when logged-in) uses your protected app
  return (
    <ProtectedRoute>
      <MainLayout />
    </ProtectedRoute>
  );
}

function AIChatExperimentRoute(): React.JSX.Element {
  const accepted =
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem(AI_CHAT_EXPERIMENT_ACK_KEY) === '1';

  if (!accepted) {
    return <Navigate to="/assistant" replace />;
  }

  return <AIChatPage />;
}

export default function AppRouter(): React.JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/loading" element={<LoadingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/email-verification" element={<VerifyEmailCallbackPage />} />
        <Route path="/email-verification/pending" element={<VerifyEmailPendingPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/password-recovery/verification" element={<ResetPasswordPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/refund" element={<RefundPage />} />
        <Route path="/ai-disclaimer" element={<AIDisclaimerPage />} />
        <Route path="/photo-consent" element={<PhotoConsentPage />} />
        <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/data-retention" element={<DataRetentionPage />} />
        <Route path="/complete-profile" element={<ProfileCompletionPage />} />
        <Route path="/public-support" element={<PublicSupportPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/delete-account/confirm" element={<DeleteAccountConfirmPage />} />
        <Route path="/delete-account/cancel" element={<DeleteAccountCancelPage />} />
        <Route path="/landing-new" element={<LandingPageNew />} />
        <Route path="/kivo-ball-demo" element={<KivoBallDemoPage />} />
        {/* Root branch handles BOTH landing (logged-out "/") and the whole app */}
        <Route path="/" element={<RootShell />}>
          {/* Your app's existing nested routes stay the same */}
          <Route index element={<HomePage />} />
          <Route path="new" element={<NewHubPage />} />
          <Route path="analyses/:id/pending" element={<PendingPage />} />
          <Route path="analyses/:id" element={<AnalysisResultPage />} />
          <Route path="analyses/new" element={<NewAnalysisPage />} />
          <Route path="history" element={<HistoryPage />} />
          <Route path="ingredients" element={<IngredientsPage />} />
          <Route path="ingredients/:id" element={<IngredientDetailsPage />} />
          <Route path="plans" element={<PlansPage />} />
          <Route path="wallet" element={<WalletPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="profile/edit" element={<ProfileEditPage />} />
          <Route path="profile/plan" element={<ProfilePlanPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="guide" element={<CaptureGuidePage />} />
          <Route path="scanner" element={<ProductScanPage />} />
          <Route path="scanner/result/:id" element={<ProductResultPage />} />
          <Route path="scanner/how-it-works" element={<ProductScannerInfoPage />} />
          <Route path="compatibility" element={<ProductCompatibilityScanPage />} />
          <Route path="compatibility/result" element={<ProductCompatibilityResultPage />} />
          <Route path="comparison" element={<ProductComparisonScanPage />} />
          <Route path="comparison/result" element={<ProductComparisonResultPage />} />
          <Route path="favorites" element={<FavoritesPage />} />
          <Route path="favorites/:id" element={<FavoriteProductDetailPage />} />
          <Route path="weather" element={<WeatherScanPage />} />
          <Route path="weather/result" element={<WeatherResultPage />} />

          {/* Premium Analysis */}
          <Route path="premium-analysis/new" element={<PremiumAnalysisPage />} />
          <Route path="premium-analysis/:id/pending" element={<PendingPage jobType="premium" />} />
          <Route path="premium-analysis/:id" element={<PremiumAnalysisResultPage />} />

          {/* Hair Analysis */}
          <Route path="hair-analysis/new" element={<HairAnalysisPage />} />
          <Route path="hair-analysis/:id/pending" element={<PendingPage jobType="hair" />} />
          <Route path="hair-analysis/:id" element={<HairAnalysisResultPage />} />

          {/* Makeup Analysis */}
          <Route path="makeup-analysis/new" element={<MakeupAnalysisPage />} />
          <Route path="makeup-analysis/:id/pending" element={<PendingPage jobType="makeup" />} />
          <Route path="makeup-analysis/:id" element={<MakeupAnalysisResultPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/:id" element={<FavoriteProductDetailPage />} />
          <Route path="ingredients" element={<IngredientsPage />} />
          <Route path="settings/data-export/download/:exportId" element={<DataExportDownloadPage />} />
          <Route path="assistant" element={<AssistantExperimentalGatePage />} />
          <Route path="assistant/chat" element={<AIChatExperimentRoute />} />
        </Route>

        <Route path="premium-analysis/:id/report" element={
          <ProtectedRoute>
            <PremiumAnalysisPDFPage />
          </ProtectedRoute>
        } />

        <Route path="analyses/:id/report" element={
          <ProtectedRoute>
            <AnalysisPDFPage />
          </ProtectedRoute>
        } />

        <Route path="hair-analysis/:id/report" element={
          <ProtectedRoute>
            <HairAnalysisPDFPage />
          </ProtectedRoute>
        } />

        <Route path="makeup-analysis/:id/report" element={
          <ProtectedRoute>
            <MakeupAnalysisPDFPage />
          </ProtectedRoute>
        } />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
